import type { ImageStyle, TextStyle, ViewStyle } from 'react-native';
import type { AppColors } from './palettes';

const CARD_BACKGROUNDS = (colors: AppColors) =>
  new Set<string>([colors.surface, colors.background.paper]);

/** Subtle card outline — visible in dark mode, invisible in light. */
export function cardEdgeStyle(colors: AppColors): ViewStyle {
  if (colors.cardEdge === 'transparent') {
    return {};
  }
  return {
    borderWidth: 1,
    borderColor: colors.cardEdge,
  };
}

function isFormFieldStyle(style: ViewStyle): boolean {
  return style.fontSize != null || style.textAlign === 'center';
}

function isCardLikeStyle(style: ViewStyle, colors: AppColors): boolean {
  const bg = style.backgroundColor;
  if (!bg || !CARD_BACKGROUNDS(colors).has(String(bg))) {
    return false;
  }
  if (style.borderRadius == null && style.borderTopLeftRadius == null) {
    return false;
  }
  if (isFormFieldStyle(style)) {
    return false;
  }
  return true;
}

/** Apply dark-mode card edges to themed StyleSheet card surfaces. */
export function applyCardEdgeToStyles<
  T extends Record<string, ViewStyle | TextStyle | ImageStyle>,
>(styles: T, colors: AppColors): T {
  if (colors.cardEdge === 'transparent') {
    return styles;
  }

  const edge = cardEdgeStyle(colors);
  const next = { ...styles } as T;

  for (const key of Object.keys(styles) as (keyof T)[]) {
    const style = styles[key];
    if (!style || typeof style !== 'object' || Array.isArray(style)) {
      continue;
    }
    const viewStyle = style as ViewStyle;
    if (!isCardLikeStyle(viewStyle, colors)) {
      continue;
    }
    next[key] = {
      ...viewStyle,
      ...edge,
      borderLeftWidth: viewStyle.borderLeftWidth ?? edge.borderWidth,
      borderLeftColor:
        viewStyle.borderLeftWidth != null ? viewStyle.borderLeftColor : edge.borderColor,
    } as T[keyof T];
  }

  return next;
}
